import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { AppError } from '@vk-hr-tek/core/error';
import { showCustomNotification } from '@vk-hr-tek/core/notifications';
import { History } from '@vk-hr-tek/core/history';

import { DynamicEntity } from '@app/types';
import {
  DashboardService,
  GetUserWidgetNewsResponse,
  GetUserWidgetNewsParams,
} from '@app/gen/dashboard';
import { ThunkExtra } from '@app/store';

import { NewsWidgetSettingsDto } from '../../../dto';
import { DashboardState } from '../../dashboard.state';

export const getNewsWidgetSettings = createAsyncThunk<
  GetUserWidgetNewsResponse,
  GetUserWidgetNewsParams,
  ThunkExtra
>(
  'newsWidget/getNewsWidgetSettings',
  async (params, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(DashboardService);

      const result = await service.getUserWidgetNews({ params });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const setNewsWidgetSettings = createAsyncThunk<
  void,
  {
    values: NewsWidgetSettingsDto;
    actions: {
      resolve: (value: unknown) => void;
      reject: (value: unknown) => void;
    };
  },
  ThunkExtra
>(
  'newsWidget/setUserWidgetSettings',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const history = inject<History>(History);
      const service = inject(DashboardService);

      await service.updateUserWidgetNews({
        body: {
          custom_name: values.name,
          has_filter: JSON.parse(values.hasFilter),
        },
      });

      actions.resolve(null);
      history.push('/');
      dispatch(
        showCustomNotification([
          {
            title: 'Виджет успешно сохранён!',
            message: 'Новости',
            type: 'info',
          },
        ]),
      );
    } catch (err) {
      const history = inject<History>(History);

      actions.reject({ [FORM_ERROR]: err });
      history.push('/');
      dispatch(
        showCustomNotification([
          {
            title: 'Не удалось сохранить виджет',
            message: 'Новости',
            type: 'error',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const newsWidgetSettingsReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<DynamicEntity, string> & DashboardState
  >,
) => {
  builder.addCase(getNewsWidgetSettings.pending, (state) => {
    state.newsSettings.status = 'loading';
    state.newsSettings.hasFilter = false;
    state.newsSettings.error = null;
  });
  builder.addCase(getNewsWidgetSettings.fulfilled, (state, { payload }) => {
    state.newsSettings.status = 'complete';
    state.newsSettings.hasFilter = !!payload.has_filter;
    state.newsSettings.name = payload.custom_name;
    state.newsSettings.isFilterAvailable = !!payload.company_has_filter;
    state.newsSettings.error = null;
  });
  builder.addCase(
    getNewsWidgetSettings.rejected,
    (state, { payload, error }) => {
      state.newsSettings.status = 'failed';
      state.newsSettings.hasFilter = false;
      state.newsSettings.name = undefined;
      state.newsSettings.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );
};
